// AdminCertificadoBuilder.js — Constructor visual de certificados por proyecto
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { proyectoService, certificadoService, consultaService, BACKEND_BASE } from '../../services/api';
import { downloadBlob } from '../../utils/downloadHelper';
import {
  ADMIN_COLORS,
  AdminButton,
  AdminSubTabContainer,
  AdminSubTab,
  AdminFormGroup,
  AdminLabel,
  AdminInput,
  AdminSelect,
  AdminBadge,
  AdminCard,
  AdminTable,
  AdminActionBtn,
  AdminEmptyState
} from './AdminShared';

const VARIABLES = [
  { key: 'nombre', label: 'Nombre del estudiante', example: 'Juan Pérez García' },
  { key: 'proyecto', label: 'Nombre del proyecto', example: 'Diplomado en Farmacología' },
  { key: 'fecha', label: 'Fecha de emisión', example: '13/03/2026' },
  { key: 'codigo', label: 'Código de verificación', example: 'A1B2C3D4E5F6' },
  { key: 'contenidos', label: 'Contenidos completados', example: 'Farmacología, Anatomía, Fisiología' },
];

const DEFAULT_CAMPO = {
  fontSize: 20,
  fontFamily: 'serif',
  color: '#1a1a1a',
  fontWeight: 'normal',
  textAlign: 'center',
};

// ============================================================
// STYLED
// ============================================================

const BuilderWrap = styled.div`
  background: ${ADMIN_COLORS.white};
  border-radius: 12px;
  padding: 1.5rem;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${ADMIN_COLORS.accent};
  cursor: pointer;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  &:hover { text-decoration: underline; }
`;

const CanvasWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${p => p.$landscape ? '297 / 210' : '210 / 297'};
  background: #f0f0f0;
  border: 2px dashed ${ADMIN_COLORS.border};
  border-radius: 8px;
  overflow: hidden;
  cursor: crosshair;
  background-size: cover;
  background-position: center;
`;

const DragField = styled.div`
  position: absolute;
  cursor: move;
  padding: 4px 8px;
  border: ${p => p.$selected ? `2px solid ${ADMIN_COLORS.accent}` : '1px dashed rgba(0,0,0,0.3)'};
  background: ${p => p.$selected ? 'rgba(20,182,203,0.1)' : 'rgba(255,255,255,0.7)'};
  border-radius: 4px;
  white-space: nowrap;
  user-select: none;
  z-index: ${p => p.$selected ? 10 : 1};
  transition: border-color 0.15s;
`;

const PropsPanel = styled.div`
  background: ${ADMIN_COLORS.bg};
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const PropRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
`;

const VarChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.4rem 0.8rem;
  border: 1px solid ${p => p.$active ? ADMIN_COLORS.accent : ADMIN_COLORS.border};
  background: ${p => p.$active ? '#e0f7fa' : ADMIN_COLORS.white};
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.82rem;
  margin: 0.25rem;
  &:hover { border-color: ${ADMIN_COLORS.accent}; }
`;

const ReqRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: ${ADMIN_COLORS.white};
  border-radius: 8px;
  margin-bottom: 0.5rem;
  border: 1px solid ${ADMIN_COLORS.borderLight};
`;

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${p => p.$landscape ? '297 / 210' : '210 / 297'};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const EmitTable = styled(AdminTable)`
  th, td {
    padding: 0.6rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid ${ADMIN_COLORS.borderLight};
    font-size: 0.85rem;
  }
  th {
    background: ${ADMIN_COLORS.bg};
    color: ${ADMIN_COLORS.primary};
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  tr:hover td { background: ${ADMIN_COLORS.bgHover}; }
`;

const ZipBtn = styled(AdminButton)`
  background: linear-gradient(135deg, ${ADMIN_COLORS.primary}, ${ADMIN_COLORS.accent});
`;

// ============================================================
// COMPONENT
// ============================================================

const AdminCertificadoBuilder = ({ proyecto, onBack }) => {
  const [tab, setTab] = useState('diseno');
  const [certConfig, setCertConfig] = useState({
    imagen_fondo: '',
    formato: 'landscape',
    campos: [],
    requisitos: [],
    activo: false,
  });
  const [selectedField, setSelectedField] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emitidos, setEmitidos] = useState([]);
  const [emitidosLoading, setEmitidosLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const canvasRef = useRef(null);
  const dragRef = useRef({ dragging: false, idx: -1, offsetX: 0, offsetY: 0 });

  // Load existing config
  useEffect(() => {
    const existing = proyecto?.config_json?.certificado;
    if (existing) {
      setCertConfig(prev => ({ ...prev, ...existing }));
    }
    // Load documentos del proyecto (filtrados por proyecto_documentos)
    consultaService.getDocumentos().then(res => {
      const docs = res.data?.documentos || res.data || [];
      const allDocs = Array.isArray(docs) ? docs : [];
      // Filtrar solo docs asignados a este proyecto
      const proyDocIds = (proyecto?.documentos || []).map(d => d.id || d.documento_id);
      setDocumentos(proyDocIds.length > 0 ? allDocs.filter(d => proyDocIds.includes(d.id)) : allDocs);
    }).catch(e => console.error('Error cargando documentos:', e));
  }, [proyecto]);

  // ============ SAVE ============
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await proyectoService.updateJSON({
        id: proyecto.id,
        config_json: { certificado: certConfig }
      });
      toast.success('Certificado guardado');
    } catch (e) {
      toast.error('Error al guardar');
    }
    setSaving(false);
  }, [certConfig, proyecto]);

  // ============ LOAD EMITIDOS ============
  const loadEmitidos = useCallback(async () => {
    setEmitidosLoading(true);
    try {
      const res = await certificadoService.porProyecto(proyecto.id);
      setEmitidos(res.data?.certificados || []);
    } catch (e) {
      toast.error('Error cargando certificados emitidos');
    }
    setEmitidosLoading(false);
  }, [proyecto]);

  // Auto-load when switching to emitidos tab
  useEffect(() => {
    if (tab === 'emitidos') loadEmitidos();
  }, [tab, loadEmitidos]);

  // ============ DOWNLOAD INDIVIDUAL ============
  const handleDescargarUno = async (certId, nombreUsuario) => {
    setDownloadingId(certId);
    try {
      const res = await certificadoService.descargarAdmin(certId);
      const nombreLimpio = (nombreUsuario || 'usuario').replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/ /g, '_');
      downloadBlob(res.data, `certificado_${nombreLimpio}.pdf`);
    } catch (e) {
      toast.error('Error descargando certificado');
    }
    setDownloadingId(null);
  };

  // ============ DOWNLOAD ZIP ============
  const handleDescargarZip = async () => {
    setDownloadingZip(true);
    try {
      const res = await certificadoService.descargarZip(proyecto.id);
      const proySlug = (proyecto.nombre || 'proyecto').replace(/[^a-zA-Z0-9_\-]/g, '_');
      downloadBlob(res.data, `certificados_${proySlug}.zip`, 'application/zip');
      toast.success(`${emitidos.length} certificados descargados`);
    } catch (e) {
      toast.error('Error descargando ZIP');
    }
    setDownloadingZip(false);
  };

  // ============ EMITIR PENDIENTES ============
  const handleEmitirPendientes = async () => {
    setEmitting(true);
    try {
      const res = await certificadoService.emitirPendientes(proyecto.id);
      const d = res.data;
      toast.success(
        `Revisados: ${d.usuarios_revisados} | Nuevos: ${d.nuevos_emitidos} | Ya existían: ${d.ya_existian} | No cumplen: ${d.no_cumplen}`
      );
      if (d.nuevos_emitidos > 0) loadEmitidos();
    } catch (e) {
      toast.error('Error emitiendo certificados');
    }
    setEmitting(false);
  };

  // ============ IMAGE UPLOAD ============
  const handleUploadBg = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await certificadoService.uploadBackground(proyecto.id, file);
      const url = res.data.url;
      setCertConfig(prev => ({ ...prev, imagen_fondo: url }));
      toast.success('Imagen subida');
    } catch (err) {
      toast.error('Error subiendo imagen');
    }
    setUploading(false);
  };

  // ============ ADD VARIABLE ============
  const addVariable = (varKey) => {
    const exists = certConfig.campos.find(c => c.variable === varKey);
    if (exists) {
      toast.info('Ya existe este campo');
      return;
    }
    const newCampo = { variable: varKey, x: 50, y: 50, ...DEFAULT_CAMPO };
    setCertConfig(prev => ({ ...prev, campos: [...prev.campos, newCampo] }));
    setSelectedField(certConfig.campos.length);
  };

  const removeVariable = (idx) => {
    setCertConfig(prev => ({
      ...prev,
      campos: prev.campos.filter((_, i) => i !== idx)
    }));
    setSelectedField(null);
  };

  // ============ DRAG ============
  const handleMouseDown = (e, idx) => {
    e.stopPropagation();
    setSelectedField(idx);
    const rect = canvasRef.current.getBoundingClientRect();
    const fieldX = (certConfig.campos[idx].x / 100) * rect.width;
    const fieldY = (certConfig.campos[idx].y / 100) * rect.height;
    dragRef.current = {
      dragging: true,
      idx,
      offsetX: e.clientX - rect.left - fieldX,
      offsetY: e.clientY - rect.top - fieldY,
    };
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left - dragRef.current.offsetX) / rect.width) * 100;
    const y = ((e.clientY - rect.top - dragRef.current.offsetY) / rect.height) * 100;
    const clampX = Math.max(0, Math.min(100, x));
    const clampY = Math.max(0, Math.min(100, y));

    setCertConfig(prev => {
      const campos = [...prev.campos];
      campos[dragRef.current.idx] = { ...campos[dragRef.current.idx], x: Math.round(clampX * 10) / 10, y: Math.round(clampY * 10) / 10 };
      return { ...prev, campos };
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // ============ UPDATE FIELD PROP ============
  const updateFieldProp = (prop, value) => {
    if (selectedField === null) return;
    setCertConfig(prev => {
      const campos = [...prev.campos];
      campos[selectedField] = { ...campos[selectedField], [prop]: value };
      return { ...prev, campos };
    });
  };

  // ============ REQUISITOS ============
  const toggleRequisito = (doc) => {
    setCertConfig(prev => {
      const existing = prev.requisitos.findIndex(r => r.documento_id === doc.id);
      if (existing >= 0) {
        return { ...prev, requisitos: prev.requisitos.filter((_, i) => i !== existing) };
      }
      return {
        ...prev,
        requisitos: [...prev.requisitos, {
          documento_id: doc.id,
          titulo: doc.titulo,
          modos: ['evaluacion']
        }]
      };
    });
  };

  const toggleModo = (docId, modo) => {
    setCertConfig(prev => {
      const reqs = prev.requisitos.map(r => {
        if (r.documento_id !== docId) return r;
        const modos = r.modos.includes(modo)
          ? r.modos.filter(m => m !== modo)
          : [...r.modos, modo];
        return { ...r, modos };
      });
      return { ...prev, requisitos: reqs };
    });
  };

  // ============ RENDER ============
  const bgStyle = certConfig.imagen_fondo
    ? { backgroundImage: `url(${BACKEND_BASE}/${certConfig.imagen_fondo})` }
    : {};

  const getVarExample = (key) => VARIABLES.find(v => v.key === key)?.example || key;
  const getVarLabel = (key) => VARIABLES.find(v => v.key === key)?.label || key;

  const selectedCampo = selectedField !== null ? certConfig.campos[selectedField] : null;

  return (
    <BuilderWrap>
      <BackBtn onClick={onBack}>&larr; Volver a Proyectos</BackBtn>
      <h3 style={{ color: ADMIN_COLORS.primary, marginBottom: '0.5rem' }}>
        Certificado: {proyecto.nombre}
      </h3>

      <ToggleRow>
        <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Certificado activo:</label>
        <input
          type="checkbox"
          checked={certConfig.activo}
          onChange={e => setCertConfig(prev => ({ ...prev, activo: e.target.checked }))}
        />
        <AdminBadge $bg={certConfig.activo ? '#d1fae5' : '#fee2e2'} $color={certConfig.activo ? '#065f46' : '#991b1b'}>
          {certConfig.activo ? 'Activo' : 'Inactivo'}
        </AdminBadge>
      </ToggleRow>

      <AdminSubTabContainer>
        {['diseno', 'requisitos', 'preview', 'emitidos'].map(t => (
          <AdminSubTab key={t} $active={tab === t} onClick={() => setTab(t)}>
            {{ diseno: 'Diseño', requisitos: 'Requisitos', preview: 'Vista Previa', emitidos: 'Emitidos' }[t]}
          </AdminSubTab>
        ))}
      </AdminSubTabContainer>

      {/* ==================== TAB DISEÑO ==================== */}
      {tab === 'diseno' && (
        <>
          <AdminFormGroup>
            <AdminLabel>Imagen de fondo</AdminLabel>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <input type="file" accept="image/*" onChange={handleUploadBg} disabled={uploading} />
              {uploading && <span style={{ color: ADMIN_COLORS.textLight }}>Subiendo...</span>}
            </div>
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminLabel>Formato</AdminLabel>
            <AdminSelect
              value={certConfig.formato}
              onChange={e => setCertConfig(prev => ({ ...prev, formato: e.target.value }))}
              style={{ width: 200 }}
            >
              <option value="landscape">Horizontal (Landscape)</option>
              <option value="portrait">Vertical (Portrait)</option>
            </AdminSelect>
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminLabel>Variables (click para agregar al canvas)</AdminLabel>
            <div>
              {VARIABLES.map(v => {
                const active = certConfig.campos.some(c => c.variable === v.key);
                return (
                  <VarChip key={v.key} $active={active} onClick={() => addVariable(v.key)}>
                    {`{{${v.key}}}`} {active && '✓'}
                  </VarChip>
                );
              })}
            </div>
          </AdminFormGroup>

          <AdminLabel>Canvas (arrastra los campos para posicionar)</AdminLabel>
          <CanvasWrap
            ref={canvasRef}
            $landscape={certConfig.formato === 'landscape'}
            style={bgStyle}
            onClick={() => setSelectedField(null)}
          >
            {certConfig.campos.map((campo, idx) => (
              <DragField
                key={idx}
                $selected={selectedField === idx}
                style={{
                  left: `${campo.x}%`,
                  top: `${campo.y}%`,
                  transform: 'translateX(-50%)',
                  fontSize: `${Math.max(10, campo.fontSize * 0.6)}px`,
                  fontFamily: campo.fontFamily,
                  color: campo.color,
                  fontWeight: campo.fontWeight,
                }}
                onMouseDown={e => handleMouseDown(e, idx)}
                onClick={e => { e.stopPropagation(); setSelectedField(idx); }}
              >
                {`{{${campo.variable}}}`}{campo.splitLines === 'dos' && ' (2L)'}
              </DragField>
            ))}
          </CanvasWrap>

          {selectedCampo && (
            <PropsPanel>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ color: ADMIN_COLORS.primary }}>
                  Propiedades: {getVarLabel(selectedCampo.variable)}
                </strong>
                <AdminButton $danger onClick={() => removeVariable(selectedField)} style={{ padding: '0.25rem 0.5rem', fontSize: '0.78rem' }}>
                  Eliminar campo
                </AdminButton>
              </div>
              <PropRow>
                <AdminFormGroup>
                  <AdminLabel>Tamaño (pt)</AdminLabel>
                  <AdminInput
                    type="number"
                    min="8" max="72"
                    value={selectedCampo.fontSize}
                    onChange={e => updateFieldProp('fontSize', parseInt(e.target.value) || 16)}
                  />
                </AdminFormGroup>
                <AdminFormGroup>
                  <AdminLabel>Fuente</AdminLabel>
                  <AdminSelect value={selectedCampo.fontFamily} onChange={e => updateFieldProp('fontFamily', e.target.value)}>
                    <option value="serif">Serif</option>
                    <option value="sans-serif">Sans-serif</option>
                    <option value="monospace">Monospace</option>
                  </AdminSelect>
                </AdminFormGroup>
                <AdminFormGroup>
                  <AdminLabel>Color</AdminLabel>
                  <AdminInput type="color" value={selectedCampo.color} onChange={e => updateFieldProp('color', e.target.value)} />
                </AdminFormGroup>
                <AdminFormGroup>
                  <AdminLabel>Peso</AdminLabel>
                  <AdminSelect value={selectedCampo.fontWeight} onChange={e => updateFieldProp('fontWeight', e.target.value)}>
                    <option value="normal">Normal</option>
                    <option value="bold">Bold</option>
                  </AdminSelect>
                </AdminFormGroup>
                <AdminFormGroup>
                  <AdminLabel>Alineación</AdminLabel>
                  <AdminSelect value={selectedCampo.textAlign} onChange={e => updateFieldProp('textAlign', e.target.value)}>
                    <option value="center">Centro</option>
                    <option value="left">Izquierda</option>
                    <option value="right">Derecha</option>
                  </AdminSelect>
                </AdminFormGroup>
                <AdminFormGroup>
                  <AdminLabel>X (%)</AdminLabel>
                  <AdminInput
                    type="number"
                    min="0" max="100" step="0.5"
                    value={selectedCampo.x}
                    onChange={e => updateFieldProp('x', parseFloat(e.target.value) || 50)}
                  />
                </AdminFormGroup>
                <AdminFormGroup>
                  <AdminLabel>Y (%)</AdminLabel>
                  <AdminInput
                    type="number"
                    min="0" max="100" step="0.5"
                    value={selectedCampo.y}
                    onChange={e => updateFieldProp('y', parseFloat(e.target.value) || 50)}
                  />
                </AdminFormGroup>
                {selectedCampo.variable === 'nombre' && (
                  <AdminFormGroup>
                    <AdminLabel>Formato nombre</AdminLabel>
                    <AdminSelect value={selectedCampo.splitLines || 'una'} onChange={e => updateFieldProp('splitLines', e.target.value)}>
                      <option value="una">Una línea</option>
                      <option value="dos">Dos líneas (nombres / apellidos)</option>
                    </AdminSelect>
                  </AdminFormGroup>
                )}
              </PropRow>
            </PropsPanel>
          )}
        </>
      )}

      {/* ==================== TAB REQUISITOS ==================== */}
      {tab === 'requisitos' && (
        <>
          <p style={{ color: ADMIN_COLORS.textLight, fontSize: '0.9rem', marginBottom: '1rem' }}>
            Selecciona los contenidos que el estudiante debe completar para obtener el certificado.
          </p>
          {documentos.length === 0 && (
            <p style={{ color: ADMIN_COLORS.textMuted }}>No se encontraron documentos.</p>
          )}
          {documentos.map(doc => {
            const req = certConfig.requisitos.find(r => r.documento_id === doc.id);
            const included = !!req;
            const modos = ['mentor', 'evaluacion', 'consulta', 'reto'];
            const modosLabels = { mentor: 'Mentor', evaluacion: 'Evaluación', consulta: 'Consulta', reto: 'Reto' };
            return (
              <ReqRow key={doc.id}>
                <input
                  type="checkbox"
                  checked={included}
                  onChange={() => toggleRequisito(doc)}
                />
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: '0.9rem' }}>{doc.titulo}</strong>
                  {included && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {modos.map(m => (
                        <VarChip
                          key={m}
                          $active={req.modos.includes(m)}
                          onClick={() => toggleModo(doc.id, m)}
                          style={{ fontSize: '0.78rem' }}
                        >
                          {modosLabels[m]} {req.modos.includes(m) ? '✓' : ''}
                        </VarChip>
                      ))}
                    </div>
                  )}
                </div>
              </ReqRow>
            );
          })}
        </>
      )}

      {/* ==================== TAB PREVIEW ==================== */}
      {tab === 'preview' && (
        <>
          <p style={{ color: ADMIN_COLORS.textLight, fontSize: '0.9rem', marginBottom: '1rem' }}>
            Vista previa con datos de ejemplo.
          </p>
          <PreviewContainer
            $landscape={certConfig.formato === 'landscape'}
            style={bgStyle}
          >
            {certConfig.campos.map((campo, idx) => {
              const example = getVarExample(campo.variable);
              const isSplit = campo.variable === 'nombre' && campo.splitLines === 'dos';
              const parts = isSplit ? example.split(' ') : [example];
              const line1 = isSplit ? parts.slice(0, Math.ceil(parts.length / 2)).join(' ') : example;
              const line2 = isSplit ? parts.slice(Math.ceil(parts.length / 2)).join(' ') : '';
              return (
                <div
                  key={idx}
                  style={{
                    position: 'absolute',
                    left: `${campo.x}%`,
                    top: `${campo.y}%`,
                    transform: 'translateX(-50%)',
                    fontSize: `${campo.fontSize * 0.7}px`,
                    fontFamily: campo.fontFamily,
                    color: campo.color,
                    fontWeight: campo.fontWeight,
                    textAlign: campo.textAlign,
                    lineHeight: isSplit ? 1.4 : 1,
                  }}
                >
                  {line1}{isSplit && <><br />{line2}</>}
                </div>
              );
            })}
          </PreviewContainer>

          {certConfig.requisitos.length > 0 && (
            <AdminCard style={{ marginTop: '1rem' }}>
              <strong style={{ color: ADMIN_COLORS.primary }}>Requisitos configurados:</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem', color: ADMIN_COLORS.text, fontSize: '0.9rem' }}>
                {certConfig.requisitos.map((r, i) => (
                  <li key={i}>{r.titulo} — {r.modos.join(', ')}</li>
                ))}
              </ul>
            </AdminCard>
          )}
        </>
      )}

      {/* ==================== TAB EMITIDOS ==================== */}
      {tab === 'emitidos' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <p style={{ color: ADMIN_COLORS.textLight, fontSize: '0.9rem', margin: 0 }}>
              {emitidos.length} certificado{emitidos.length !== 1 ? 's' : ''} emitido{emitidos.length !== 1 ? 's' : ''}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <AdminButton $success onClick={handleEmitirPendientes} disabled={emitting}>
                {emitting ? 'Verificando...' : 'Emitir pendientes'}
              </AdminButton>
              {emitidos.length > 0 && (
                <ZipBtn onClick={handleDescargarZip} disabled={downloadingZip}>
                  {downloadingZip ? 'Generando ZIP...' : 'Descargar todos (ZIP)'}
                </ZipBtn>
              )}
            </div>
          </div>

          {emitidosLoading ? (
            <p style={{ color: ADMIN_COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
          ) : emitidos.length === 0 ? (
            <AdminEmptyState>No hay certificados emitidos para este proyecto.</AdminEmptyState>
          ) : (
            <EmitTable>
              <thead>
                <tr>
                  <th>Estudiante</th>
                  <th>Email</th>
                  <th>Fecha</th>
                  <th>Código</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {emitidos.map(cert => (
                  <tr key={cert.id}>
                    <td style={{ fontWeight: 600 }}>{cert.usuario_nombre}</td>
                    <td style={{ color: ADMIN_COLORS.textLight }}>{cert.usuario_email}</td>
                    <td>{new Date(cert.fecha_emision).toLocaleDateString('es-ES')}</td>
                    <td>
                      <code style={{ background: '#f0fdf4', color: '#065f46', padding: '2px 6px', borderRadius: 4, fontSize: '0.82rem' }}>
                        {cert.codigo_verificacion}
                      </code>
                    </td>
                    <td>
                      <AdminActionBtn
                        onClick={() => handleDescargarUno(cert.id, cert.usuario_nombre)}
                        disabled={downloadingId === cert.id}
                      >
                        {downloadingId === cert.id ? 'Descargando...' : 'Descargar PDF'}
                      </AdminActionBtn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </EmitTable>
          )}
        </>
      )}

      {/* ==================== SAVE BUTTON ==================== */}
      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
        <AdminButton $ghost onClick={onBack}>Cancelar</AdminButton>
        <AdminButton onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar Certificado'}
        </AdminButton>
      </div>
    </BuilderWrap>
  );
};

export default AdminCertificadoBuilder;
